import type { Coords, ItemReference, SmartGuide } from 'src/types';

export interface LayoutElement {
  ref: ItemReference;
  from: Coords;
  to: Coords;
}

export type AlignAction =
  | 'LEFT'
  | 'CENTER_X'
  | 'RIGHT'
  | 'TOP'
  | 'CENTER_Y'
  | 'BOTTOM';

export type DistributionAction = 'HORIZONTAL' | 'VERTICAL';

export interface LayoutMove {
  ref: ItemReference;
  delta: Coords;
}

export interface SnapResult {
  delta: Coords;
  guides: SmartGuide[];
}

interface SnapMatch {
  adjustment: number;
  value: number;
  element: LayoutElement;
}

interface LayoutScene {
  items: Array<{ id: string; tile: Coords }>;
  rectangles: Array<{ id: string; from: Coords; to: Coords }>;
  textBoxes: Array<{ id: string; tile: Coords; size?: { width: number } }>;
}

export const getLayoutElement = (
  ref: ItemReference,
  scene: LayoutScene
): LayoutElement | null => {
  if (ref.type === 'ITEM') {
    const item = scene.items.find((candidate) => candidate.id === ref.id);
    return item ? { ref, from: { ...item.tile }, to: { ...item.tile } } : null;
  }
  if (ref.type === 'RECTANGLE') {
    const rectangle = scene.rectangles.find((candidate) => candidate.id === ref.id);
    return rectangle ? {
      ref,
      from: {
        x: Math.min(rectangle.from.x, rectangle.to.x),
        y: Math.min(rectangle.from.y, rectangle.to.y)
      },
      to: {
        x: Math.max(rectangle.from.x, rectangle.to.x),
        y: Math.max(rectangle.from.y, rectangle.to.y)
      }
    } : null;
  }
  if (ref.type === 'TEXTBOX') {
    const textBox = scene.textBoxes.find((candidate) => candidate.id === ref.id);
    return textBox ? {
      ref,
      from: { ...textBox.tile },
      to: { x: textBox.tile.x + (textBox.size?.width ?? 2), y: textBox.tile.y }
    } : null;
  }
  return null;
};

const centerX = (element: LayoutElement) => (element.from.x + element.to.x) / 2;
const centerY = (element: LayoutElement) => (element.from.y + element.to.y) / 2;

export const alignElements = (
  elements: LayoutElement[],
  action: AlignAction
): LayoutMove[] => {
  if (elements.length < 2) return [];
  const target = action === 'LEFT'
    ? Math.min(...elements.map((element) => element.from.x))
    : action === 'RIGHT'
      ? Math.max(...elements.map((element) => element.to.x))
      : action === 'TOP'
        ? Math.min(...elements.map((element) => element.from.y))
        : action === 'BOTTOM'
          ? Math.max(...elements.map((element) => element.to.y))
          : action === 'CENTER_X'
            ? (Math.min(...elements.map((element) => element.from.x)) +
              Math.max(...elements.map((element) => element.to.x))) / 2
            : (Math.min(...elements.map((element) => element.from.y)) +
              Math.max(...elements.map((element) => element.to.y))) / 2;

  return elements.map((element) => ({
    ref: element.ref,
    delta: action === 'LEFT'
      ? { x: target - element.from.x, y: 0 }
      : action === 'RIGHT'
        ? { x: target - element.to.x, y: 0 }
        : action === 'CENTER_X'
          ? { x: Math.round(target - centerX(element)), y: 0 }
          : action === 'TOP'
            ? { x: 0, y: target - element.from.y }
            : action === 'BOTTOM'
              ? { x: 0, y: target - element.to.y }
              : { x: 0, y: Math.round(target - centerY(element)) }
  }));
};

export const distributeElements = (
  elements: LayoutElement[],
  action: DistributionAction
): LayoutMove[] => {
  if (elements.length < 3) return [];
  const horizontal = action === 'HORIZONTAL';
  const sorted = [...elements].sort((a, b) => horizontal
    ? a.from.x - b.from.x
    : a.from.y - b.from.y
  );
  const first = horizontal ? sorted[0].from.x : sorted[0].from.y;
  const last = horizontal
    ? sorted[sorted.length - 1].to.x
    : sorted[sorted.length - 1].to.y;
  const occupied = sorted.reduce((sum, element) => sum + (horizontal
    ? element.to.x - element.from.x
    : element.to.y - element.from.y
  ), 0);
  const gap = (last - first - occupied) / (sorted.length - 1);
  let cursor = first;

  return sorted.map((element) => {
    const current = horizontal ? element.from.x : element.from.y;
    const delta = Math.round(cursor - current);
    cursor += (horizontal
      ? element.to.x - element.from.x
      : element.to.y - element.from.y
    ) + gap;
    return {
      ref: element.ref,
      delta: horizontal ? { x: delta, y: 0 } : { x: 0, y: delta }
    };
  });
};

export const tidyElements = (elements: LayoutElement[]): LayoutMove[] => {
  if (elements.length < 2) return [];
  const spreadX = Math.max(...elements.map(centerX)) - Math.min(...elements.map(centerX));
  const spreadY = Math.max(...elements.map(centerY)) - Math.min(...elements.map(centerY));
  const horizontal = spreadX >= spreadY;
  const aligned = alignElements(elements, horizontal ? 'CENTER_Y' : 'CENTER_X');
  const distributed = distributeElements(elements, horizontal ? 'HORIZONTAL' : 'VERTICAL');
  const distributionById = new Map(distributed.map((move) => [`${move.ref.type}:${move.ref.id}`, move.delta]));

  return aligned.map((move) => {
    const distributedDelta = distributionById.get(`${move.ref.type}:${move.ref.id}`) ?? { x: 0, y: 0 };
    return {
      ref: move.ref,
      delta: {
        x: move.delta.x + distributedDelta.x,
        y: move.delta.y + distributedDelta.y
      }
    };
  });
};

const unionBounds = (elements: LayoutElement[]) => ({
  from: {
    x: Math.min(...elements.map((element) => element.from.x)),
    y: Math.min(...elements.map((element) => element.from.y))
  },
  to: {
    x: Math.max(...elements.map((element) => element.to.x)),
    y: Math.max(...elements.map((element) => element.to.y))
  }
});

const axisPoints = (from: number, to: number) => [from, (from + to) / 2, to];

/**
 * Snaps a moving selection's edges/centres to nearby object edges/centres.
 * The returned delta applies to every moved object, preserving group geometry.
 */
export const snapToObjects = (
  moving: LayoutElement[],
  stationary: LayoutElement[],
  requestedDelta: Coords,
  tolerance = 1
): SnapResult => {
  // Returning to the drag origin must always be possible. Nearby objects may
  // provide guides while moving, but they must never manufacture movement
  // when the pointer is back on the exact starting tile.
  if (requestedDelta.x === 0 && requestedDelta.y === 0) {
    return { delta: requestedDelta, guides: [] };
  }
  if (!moving.length || !stationary.length) return { delta: requestedDelta, guides: [] };
  const movingBounds = unionBounds(moving);
  const translated = {
    from: {
      x: movingBounds.from.x + requestedDelta.x,
      y: movingBounds.from.y + requestedDelta.y
    },
    to: {
      x: movingBounds.to.x + requestedDelta.x,
      y: movingBounds.to.y + requestedDelta.y
    }
  };

  const findAdjustment = (axis: 'x' | 'y'): SnapMatch | null => {
    const movingPoints = axisPoints(translated.from[axis], translated.to[axis]);
    let best: SnapMatch | null = null;
    stationary.forEach((element) => {
      axisPoints(element.from[axis], element.to[axis]).forEach((value) => {
        movingPoints.forEach((point) => {
          const adjustment = value - point;
          if (Math.abs(adjustment) <= tolerance &&
            (!best || Math.abs(adjustment) < Math.abs(best.adjustment))) {
            best = { adjustment, value, element };
          }
        });
      });
    });
    return best;
  };

  const xMatch = findAdjustment('x');
  const yMatch = findAdjustment('y');
  const delta = {
    x: requestedDelta.x + (xMatch?.adjustment ?? 0),
    y: requestedDelta.y + (yMatch?.adjustment ?? 0)
  };
  const guides: SmartGuide[] = [];
  if (xMatch) {
    guides.push({
      axis: 'X',
      value: xMatch.value,
      from: Math.min(translated.from.y + (yMatch?.adjustment ?? 0), xMatch.element.from.y),
      to: Math.max(translated.to.y + (yMatch?.adjustment ?? 0), xMatch.element.to.y)
    });
  }
  if (yMatch) {
    guides.push({
      axis: 'Y',
      value: yMatch.value,
      from: Math.min(translated.from.x + (xMatch?.adjustment ?? 0), yMatch.element.from.x),
      to: Math.max(translated.to.x + (xMatch?.adjustment ?? 0), yMatch.element.to.x)
    });
  }
  return { delta, guides };
};
